import type { DerivedReadImage, SuggestionRegion } from "../types.js";
import { ImageViewer } from "./ImageViewer.js";

/**
 * UR-6 — the per-photo READ image, full size. This is *literally* the image
 * sent to the model on the union path: one rectified whole-panel union crop
 * (the union of the geometry columns), downscaled to the sent dims. Because
 * the image on screen IS the crop we send, nothing about the crop is overlaid
 * on it — drawing the geometry/column boxes here only implied we send separate
 * columns (we don't; we send this one union crop).
 *
 * The one overlay that earns its place is the SELECTED BREAKER's box
 * (`highlightRegion`, drawn only when a breaker is picked in the reads panel).
 * That isn't describing the crop — it's the model's claimed read location, in
 * the same read-image `[0..1]` space (the model reads this image and emits
 * boxes against it, untransformed), so a support engineer can confirm the
 * model read the breaker where it actually is.
 *
 * Replaces the old per-768-tile `TileFocus`/`TilePopup` — the union path
 * never dices the panel into tiles, so there is nothing to pick.
 */
export function ReadImageView({
  read,
  photoId,
  highlightRegion,
}: {
  readonly read: DerivedReadImage;
  readonly photoId: string;
  /** The selected breaker's region, highlighted when it's on this photo. */
  readonly highlightRegion?: SuggestionRegion | null;
}) {
  // Only draw the highlight when it belongs to the photo on screen and the
  // model actually emitted a box (a degenerate 0×0 box carries no location).
  const hl =
    highlightRegion != null &&
    highlightRegion.photoId === photoId &&
    highlightRegion.box.width > 0 &&
    highlightRegion.box.height > 0
      ? highlightRegion.box
      : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-mono text-xs text-fg-3" title={photoId}>
          {photoId}
        </span>
        <span className="flex flex-none items-center gap-1.5 text-xs text-fg-3">
          {read.lowRes ? (
            <span
              aria-label="low-res read"
              title="The sent short side landed under the legibility threshold."
              className="rounded-sm bg-warning px-1 text-[10px] font-semibold leading-tight text-fg-on-accent"
            >
              low-res
            </span>
          ) : null}
          {read.sentWidth} × {read.sentHeight} · what the model read
        </span>
      </div>

      <ImageViewer
        src={read.url}
        alt="Read image"
        overlay={
          hl !== null ? (
            <span
              aria-label="breaker-box"
              className="pointer-events-none absolute rounded-sm border-2 border-warning bg-warning/30 ring-1 ring-black/40"
              style={{
                left: `${hl.x * 100}%`,
                top: `${hl.y * 100}%`,
                width: `${hl.width * 100}%`,
                height: `${hl.height * 100}%`,
              }}
            />
          ) : null
        }
      />

      <div className="flex-none">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-3">
          {hl !== null ? "Selected breaker" : "Union crop sent to the model"}
        </span>
        <p className="mt-1 text-[13px] text-fg-3">
          {hl !== null
            ? "Where the model read the selected breaker, boxed on the image it read."
            : "This is the exact image we send — pick a breaker on the right to box where it was read."}
        </p>
      </div>
    </div>
  );
}
